import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, Clock, Filter } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "overdue", label: "Overdue" },
  { key: "due", label: "Due Soon" },
  { key: "customer", label: "Customers" },
  { key: "vendor", label: "Vendors" },
];

export default function PendingPayments() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/pending/payments").then((r) => setData(r.data)); }, []);

  const rows = useMemo(() => {
    const r = data?.rows || [];
    if (filter === "overdue") return r.filter((x) => x.overdue > 0.01);
    if (filter === "due") return r.filter((x) => x.due > 0.01 && x.overdue <= 0.01);
    if (filter === "customer") return r.filter((x) => x.type === "customer");
    if (filter === "vendor") return r.filter((x) => x.type === "vendor");
    return r;
  }, [data, filter]);

  return (
    <div className="space-y-6" data-testid="pending-payments-page">
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isManager ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-4`}>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Total Receivable</div>
          <div className="data-num text-3xl mt-1 text-[#0A0A0A]" data-testid="total-receivable">
            ₹ {formatMoney(data?.total_customer_due || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">From customers</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><AlertTriangle size={12} /> Overdue</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="total-overdue">
            ₹ {formatMoney(data?.total_overdue || 0)}
          </div>
          <div className="text-xs text-[#B91C1C]/80 mt-1">Past credit period</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309] flex items-center gap-1.5"><Clock size={12} /> Due Soon</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="total-due">
            ₹ {formatMoney(data?.total_due_within || 0)}
          </div>
          <div className="text-xs text-[#B45309]/80 mt-1">Within credit period</div>
        </div>
        {!isManager && (
          <div className="card-flat p-5">
            <div className="small-label text-gray-500">Total Payable</div>
            <div className="data-num text-3xl mt-1 text-[#0A0A0A]" data-testid="total-payable">
              ₹ {formatMoney(data?.total_vendor_due || 0)}
            </div>
            <div className="text-xs text-gray-500 mt-1">To vendors</div>
          </div>
        )}
      </div>

      <div className="card-flat p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={16} className="text-gray-500" />
          <span className="small-label text-gray-500 mr-2">Filter</span>
          {FILTERS.map((f) => {
            if (f.key === "vendor" && isManager) return null;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase rounded-sm border ${filter === f.key ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"}`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 small-label text-gray-500">
          Pending balances · overdue first
        </div>
        <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Party</th>
                <th className="text-left">Type</th>
                <th className="text-left">Phone</th>
                <th className="num">Overdue</th>
                <th className="num">Due Soon</th>
                <th className="num">Balance</th>
                <th className="num">Days Old</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isCustOverdue = r.overdue > 0.01;
                return (
                  <tr key={r.party_id} className={isCustOverdue ? "bg-[#FEE2E2]/40" : ""} data-testid={`pp-row-${r.party_id}`}>
                    <td className="font-semibold">
                      {r.name}
                      {r.type === "customer" && r.credit_period_days > 0 && (
                        <span className="ml-2 text-xs text-gray-500 font-normal">· net-{r.credit_period_days}</span>
                      )}
                    </td>
                    <td className="capitalize">{r.type}</td>
                    <td className="data-num text-xs">{r.phone || "—"}</td>
                    <td className="num font-bold text-[#B91C1C]">
                      {r.overdue > 0.01 ? `₹ ${formatMoney(r.overdue)}` : ""}
                    </td>
                    <td className="num font-bold text-[#B45309]">
                      {r.due > 0.01 ? `₹ ${formatMoney(r.due)}` : ""}
                    </td>
                    <td className={`num font-bold ${r.type === "customer" ? "text-[#0A0A0A]" : "text-[#B91C1C]"}`}>
                      ₹ {formatMoney(r.balance)}
                    </td>
                    <td className="num">
                      {r.days_outstanding > 0 ? (
                        <span className={`text-xs font-bold ${isCustOverdue ? "text-[#B91C1C]" : "text-gray-600"}`}>
                          {r.days_outstanding}d
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="text-right">
                      <Link to={`/parties/${r.party_id}`} className="text-sm font-semibold hover:underline">Open →</Link>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan="8" className="py-10 text-center text-gray-400 text-sm">
                  {filter === "overdue" ? "No overdue payments 🎉" : filter === "due" ? "Nothing due soon" : "All clear 🎉"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
